import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Period {
  start: number;
  end: number;
}

// Real opening hours (src/locales/*.json -> hours.*), expressed in minutes since midnight.
const SCHEDULE: Record<number, Period[]> = {
  0: [{ start: 11 * 60 + 55, end: 15 * 60 }, { start: 18 * 60 + 55, end: 22 * 60 + 30 }], // Sunday
  1: [{ start: 11 * 60 + 55, end: 14 * 60 + 30 }, { start: 18 * 60 + 55, end: 22 * 60 + 30 }], // Monday
  2: [{ start: 11 * 60 + 55, end: 14 * 60 + 30 }, { start: 18 * 60 + 55, end: 22 * 60 + 30 }], // Tuesday
  3: [{ start: 11 * 60 + 55, end: 14 * 60 + 30 }, { start: 18 * 60 + 55, end: 22 * 60 + 30 }], // Wednesday
  4: [{ start: 11 * 60 + 55, end: 14 * 60 + 30 }, { start: 18 * 60 + 55, end: 22 * 60 + 30 }], // Thursday
  5: [{ start: 11 * 60 + 55, end: 14 * 60 + 30 }, { start: 18 * 60 + 30, end: 23 * 60 }], // Friday
  6: [{ start: 11 * 60 + 55, end: 15 * 60 }, { start: 18 * 60 + 30, end: 23 * 60 }] // Saturday
};

function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

function computeStatus(now: Date): { open: boolean; label: string; time: number } {
  const day = now.getDay();
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const todayPeriods = SCHEDULE[day];

  for (const period of todayPeriods) {
    if (minutesNow >= period.start && minutesNow < period.end) {
      return { open: true, label: 'closesAt', time: period.end };
    }
  }

  const upcomingToday = todayPeriods.find((period) => minutesNow < period.start);
  if (upcomingToday) {
    return { open: false, label: 'opensAt', time: upcomingToday.start };
  }

  const nextDay = (day + 1) % 7;
  return { open: false, label: 'opensAt', time: SCHEDULE[nextDay][0].start };
}

const OpenStatusBadge: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(() => computeStatus(new Date()));

  useEffect(() => {
    const interval = setInterval(() => setStatus(computeStatus(new Date())), 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-body tracking-wide uppercase ${
        status.open
          ? 'bg-luxury-gold/15 text-luxury-gold border border-luxury-gold/40'
          : 'bg-luxury-gray-dark/10 text-luxury-gray-dark border border-luxury-gray-dark/20'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${status.open ? 'bg-luxury-gold' : 'bg-luxury-gray'}`} />
      {status.open ? t('home.openNow') : t(`home.${status.label}`, { time: formatTime(status.time) })}
    </span>
  );
};

export default OpenStatusBadge;
